import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = { pactId: String, initialStatus: String, successUrl: String, redirectDelay: Number };
  static targets = ["step", "icon", "checkIcon", "iconContainer", "spinner", "statusIcon", "statusIconContainer"];

  declare pactIdValue: string;
  declare initialStatusValue: string;
  declare hasSuccessUrlValue: boolean;
  declare successUrlValue: string;
  declare redirectDelayValue: number;
  declare stepTargets: HTMLElement[];
  declare iconTargets: HTMLElement[];
  declare checkIconTargets: HTMLElement[];
  declare iconContainerTargets: HTMLElement[];
  declare spinnerTargets: HTMLElement[];
  declare statusIconTargets: HTMLElement[];
  declare statusIconContainerTargets: HTMLElement[];

  pollingInterval = 3000;
  timer: number | null = null;
  lastStatus: string | null = null;

  connect() {
    // Initialize UI according to passed status
    const init = this.initialStatusValue || 'draft';
    this.lastStatus = init;
    this.renderStep(this.getCurrentActiveIndex(init));

    if (this.pactIdValue) {
      this.startPolling();
    }
  }

  disconnect() {
    this.stopPolling();
  }

  startPolling() {
    if (this.timer) return;
    this.fetchAndUpdate();
    this.timer = window.setInterval(() => this.fetchAndUpdate(), this.pollingInterval);
  }

  stopPolling() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  async fetchAndUpdate() {
    try {
      // Add timestamp to avoid browser caching
      const url = `/pacts/${this.pactIdValue}.json?t=${new Date().getTime()}`;
      const res = await fetch(url, {
        headers: { 
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const data = await res.json();
      const status = data.status || data.state || 'draft';

      if (status !== this.lastStatus) {
        await this.transitionToState(status.toString());
      }

      // stop polling if terminal state
      if (['released', 'expired', 'cancelled', 'disputed'].includes(status)) {
        this.stopPolling();
      }
    } catch (err) {
      console.error('[pacts--processing] Error during fetch:', err);
    }
  }

  async transitionToState(newStatus: string) {
    const stepsOrder = ['verifying_payment', 'encrypting_data', 'processing_bank', 'confirming_agreement'];
    
    const targetIndex = this.getCurrentActiveIndex(newStatus);
    const currentIndex = this.getCurrentActiveIndex(this.lastStatus || 'draft');

    // If we are advancing, we can simulate the intermediate steps
    if (targetIndex > currentIndex) {
      for (let i = currentIndex + 1; i <= targetIndex; i++) {
        this.renderStep(i);
        if (i < targetIndex) {
          await new Promise(resolve => setTimeout(resolve, 800)); // Simulation delay
        }
      }
    } else {
      this.renderStep(targetIndex);
    }

    this.lastStatus = newStatus;

    // Redirect if payment is completed (authorized or released)
    const isSuccess = ['authorized', 'released'].includes(newStatus);
    if (isSuccess && this.successUrlValue) {
      this.stopPolling();
      setTimeout(() => {
        window.location.href = this.successUrlValue;
      }, this.redirectDelayValue || 4000);
    }
  }

  getCurrentActiveIndex(status: string): number {
    if (status === 'awaiting_auth') return 0;
    if (status === 'authorized') return 4;
    if (status === 'released') return 4;
    return -1;
  }

  renderStep(activeIndex: number) {
    const stepsOrder = ['verifying_payment', 'encrypting_data', 'processing_bank', 'confirming_agreement'];

    this.stepTargets.forEach((el, idx) => {
      const name = el.dataset.step as string;
      const stepIdx = stepsOrder.indexOf(name);

      const iconContainer = el.querySelector('[data-pacts--processing-target="iconContainer"]') as HTMLElement;
      const iconWrapper = el.querySelector('[data-pacts--processing-target="icon"]') as HTMLElement;
      const checkWrapper = el.querySelector('[data-pacts--processing-target="checkIcon"]') as HTMLElement;
      const spinner = el.querySelector('[data-pacts--processing-target="spinner"]') as HTMLElement;
      const statusIconContainer = el.querySelector('[data-pacts--processing-target="statusIconContainer"]') as HTMLElement;

      // CLEANUP / RESET
      el.classList.remove('bg-teal-400/5', 'border-teal-400/30', 'bg-violet-100', 'border-violet-700/30', 'bg-white', 'border-slate-200', 'outline', 'outline-2', 'outline-offset-[-1.82px]', 'outline-teal-400/30');
      
      if (iconContainer) {
        iconContainer.classList.remove('bg-teal-400', 'bg-gradient-to-b', 'from-violet-700', 'to-violet-500', 'bg-slate-200', 'bg-transparent', 'text-white', 'rounded-xl');
      }

      // Default Visibility (Resetting Tailwind 'hidden' class)
      if (iconWrapper) iconWrapper.classList.remove('hidden');
      if (checkWrapper) checkWrapper.classList.add('hidden');
      if (spinner) spinner.classList.add('hidden');
      if (statusIconContainer) statusIconContainer.classList.add('hidden');

      if (stepIdx < activeIndex) {
        // --- COMPLETED STATE ---
        el.classList.add('bg-teal-400/5', 'outline', 'outline-2', 'outline-offset-[-1.82px]', 'outline-teal-400/30');
        if (iconContainer) iconContainer.classList.add('bg-transparent');
        
        // Hide Document, Show Shadow Check
        if (iconWrapper) iconWrapper.classList.add('hidden');
        if (checkWrapper) checkWrapper.classList.remove('hidden');
        
        // Show Minimal Check on right
        if (statusIconContainer) statusIconContainer.classList.remove('hidden');
        
      } else if (stepIdx === activeIndex) {
        // --- ACTIVE STATE ---
        el.classList.add('bg-violet-100', 'border-violet-700/30');
        if (iconContainer) iconContainer.classList.add('bg-gradient-to-b', 'from-violet-700', 'to-violet-500', 'text-white', 'rounded-xl');
        
        // Make active icon white
        const mainSvg = iconWrapper?.querySelector('svg');
        if (mainSvg) mainSvg.classList.add('!text-white');
        
        // Show Spinner on right
        if (spinner) spinner.classList.remove('hidden');
        
      } else {
        // --- PENDING STATE ---
        el.classList.add('bg-white', 'border-slate-200');
        if (iconContainer) iconContainer.classList.add('bg-slate-200', 'rounded-xl');
        
        // Make pending icon gray
        const mainSvg = iconWrapper?.querySelector('svg');
        if (mainSvg) {
          mainSvg.classList.remove('text-white', '!text-white');
          mainSvg.classList.add('text-gray-500');
        }
      }
    });
  }

  updateState(status: string) {
    this.renderStep(this.getCurrentActiveIndex(status));
    this.lastStatus = status;
  }
}
